import { computed, ComputedRef, nextTick, Ref, ref } from "vue";
import { Store, useStore } from "vuex";
import { RootState } from "@/store/types.ts";
import { RecordInfo, EntryDocument } from "@/types/entry.ts";
import { toast } from "vue-sonner";
import { useRouter } from "vue-router";

export const key = Symbol("recordCollectionControls");

export interface RecordCollectionControlsApi {
  show: ComputedRef<boolean>;
  deleteCollection: (id: string) => Promise<void>;
  edit: (id: string) => Promise<void>;
  editingColumnId: Ref<string | null>;
  addRecord: (id: string) => Promise<void>;
  bulkEdit?: (columnID: string) => Promise<void>;
}

async function deleteRecords(
  store: Store<RootState>,
  categoryID: string,
  columnID: string
) {
  const records = store.getters["api/category"](categoryID).entries.filter(
    (item: EntryDocument) => item.recordInfo?.columnID === columnID
  );
  await Promise.all(
    records.map((record: EntryDocument) =>
      store.dispatch("api/deleteEntry", {
        categoryID,
        path: record.path,
      })
    )
  );
}

async function deleteCollection(
  store: Store<RootState>,
  categoryID: string,
  columnID: string
) {
  const collections = JSON.parse(
    JSON.stringify(
      store.getters.getStoreVar(
        "Category",
        "page",
        "recordColumns",
        "recordColumns"
      )
    )
  );
  const recordColumns = collections.filter(
    (c: RecordInfo) => c.columnID !== columnID
  );
  await store.dispatch("api/patchCategory", {
    path: `/category/${categoryID}`,
    patch: { recordColumns },
  });
  await store.dispatch("setStoreVar", {
    page: "Category",
    group: "page",
    type: "recordColumns",
    name: "recordColumns",
    value: recordColumns,
  });
}

const editingColumnId: Ref<string | null> = ref(null);

export default function createData(): RecordCollectionControlsApi {
  const store = useStore();
  const router = useRouter();
  return {
    show: computed(() => store.getters.showCMS),
    deleteCollection: async (id) => {
      if (
        confirm(
          "Are you sure you want to delete this column? All underlying records will also be deleted"
        )
      ) {
        try {
          const { categoryID } = store.state.route.params;
          if (!categoryID) {
            return;
          }
          await deleteRecords(store, categoryID, id);
          await deleteCollection(store, categoryID, id);
        } catch (err) {
          console.log(err);
          alert("We're sorry, an error occurred");
        }
      }
    },
    edit: async (id) => {
      editingColumnId.value = null;
      await nextTick(() => {
        editingColumnId.value = id;
      });
    },
    editingColumnId,
    addRecord: async (columnID: string) => {
      try {
        const recordColumns = JSON.parse(
          JSON.stringify(
            store.getters.getStoreVar(
              "Category",
              "page",
              "recordColumns",
              "recordColumns"
            )
          )
        );
        const column = recordColumns.find(
          (c: RecordInfo) => c.columnID === columnID
        );
        if (!column) {
          toast.error("Unable to find column to add record to");
          return;
        }

        const newRecord = {
          name: "New Record",
          recordValue: "",
          recordDate: "",
          yearStart: "",
          yearEnd: "",
          primaryInfo: "",
          extraInfo: "",
          description: "",
          recordImage: [],
          columnID: column.columnID,
        };

        const { categoryID } = store.state.route.params;
        if (!categoryID) {
          toast.error("No Category ID found");
          return;
        }

        const doc = {
          categoryID,
          recordInfo: newRecord,
        };

        const res = await store.dispatch("api/addEntry", {
          doc,
          categoryID,
        });
        if (res.status !== 201) {
          toast.error("Error creating new record");
        }
      } catch (err) {
        console.error(err);
        toast.error("Error creating new record");
      }
    },
    bulkEdit: async (columnID: string) => {
      const styleID = store.getters.getStyleID;
      const { categoryID } = router.currentRoute.value.params;
      router.push(
        `/bulkRecordItemsEditor/${styleID}/${categoryID}/${columnID}`
      );
    },
  };
}

const injectError = `Inject has failed for RecordCollectionControlsApi`;

export const defaultApi: RecordCollectionControlsApi = {
  show: computed(() => false),
  deleteCollection: async () => console.error(injectError),
  edit: async () => console.error(injectError),
  editingColumnId: ref(null),
  addRecord: async () => console.error(injectError),
  bulkEdit: async () => console.error(injectError),
};

export const storybookApi: RecordCollectionControlsApi = {
  show: computed(() => false),
  deleteCollection: async (id) =>
    alert(`Delete record collection with id: ${id}`),
  edit: async (id) => alert(`Edit record collection with id: ${id}`),
  editingColumnId: ref(null),
  addRecord: async (id) => alert(`Adding record to column with id: ${id}`),
  bulkEdit: async (id) => alert(`Bulk edit column with id: ${id}`),
};
